/**
 * Owner's Fund Analytics — insights from Owners Fund Statement, P&L, and balance sheet.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fetchPumpOwnerFundAnalytics } from './adminApi';
import OwnerFundMismatchDetailModal from './OwnerFundMismatchDetailModal';
import AdminPumpReportExportButton from './AdminPumpReportExportButton';
import { ADMIN_PUMP_EXPORT_IDS } from './adminPumpReportSections';
import './AdminPumpOwnerFundAnalytics.css';
import {
  formatCompactInrAxis,
  formatInrAmount,
  formatIndianNumber,
  formatReportDate,
} from '../../../utils/reportFormat';

const formatCurrency = formatInrAmount;
const formatCompact = formatCompactInrAxis;

function ChartWrap({ height = 280, children }) {
  return (
    <div className="owner-fund-chart-wrap" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  );
}

function severityClass(severity) {
  if (severity === 'critical') return 'critical';
  if (severity === 'warning') return 'warning';
  return 'info';
}

function MismatchLink({ amount, date, onOpen, className = '' }) {
  const diff = Number(amount) || 0;
  if (diff < 0.01 || !date) return <span className="text-muted">—</span>;
  return (
    <button
      type="button"
      className={`owner-fund-mismatch-link ${className}`}
      onClick={() => onOpen(date)}
      title="Click to see what went wrong on this date"
    >
      {formatCurrency(diff)}
    </button>
  );
}

export default function AdminPumpOwnerFundAnalyticsPanel({
  pumpId,
  pumpName,
  defaultFromDate = '',
  defaultToDate = '',
  onOpenAccounting,
}) {
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [mismatchModalDate, setMismatchModalDate] = useState('');

  useEffect(() => {
    setFromDate(defaultFromDate || '');
    setToDate(defaultToDate || '');
  }, [defaultFromDate, defaultToDate]);

  const load = useCallback(() => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    setError('');
    fetchPumpOwnerFundAnalytics(pumpId, fromDate, toDate)
      .then((res) => {
        if (!res.status) {
          setError(res.message || 'Unable to load analytics');
          setData(null);
          return;
        }
        setData(res);
      })
      .catch((err) => {
        setError(err.message || 'Unable to load analytics');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [pumpId, fromDate, toDate]);

  const summary = data?.summary || {};
  const pl = data?.pl_summary || {};
  const bs = data?.balance_sheet || {};
  const dailySeries = data?.daily_series || [];
  const insights = data?.insights || [];
  const mismatchDays = data?.mismatch_days || [];

  const plChartData = useMemo(
    () => (pl.categories || [])
      .filter((c) => Math.abs(c.net) >= 1)
      .slice(0, 12)
      .map((c) => ({
        name: c.name?.length > 18 ? `${c.name.slice(0, 16)}…` : c.name,
        fullName: c.name,
        net: c.net,
        fill: c.net >= 0 ? '#16a34a' : '#dc2626',
      })),
    [pl.categories]
  );

  const expensePieData = useMemo(
    () => (pl.top_expense_heads || []).map((e) => ({
      name: e.name?.length > 20 ? `${e.name.slice(0, 18)}…` : e.name,
      amount: e.amount,
    })),
    [pl.top_expense_heads]
  );

  const healthScore = summary.health_score ?? 0;
  const healthTone = healthScore >= 80 ? 'good' : healthScore >= 60 ? 'fair' : healthScore >= 40 ? 'warn' : 'bad';

  const openMismatchDetail = (date) => {
    if (date) setMismatchModalDate(date);
  };

  const handleChartMismatchClick = (barData) => {
    const payload = barData?.payload;
    if (payload?.has_mismatch && payload?.date) {
      openMismatchDetail(payload.date);
    }
  };

  const topMismatchDate = mismatchDays[0]?.date || '';

  return (
    <div className="owner-fund-analytics" id="admin-pump-owner-fund-export">
      <div className="owner-fund-analytics__intro admin-panel mb-3">
        <h6 className="mb-1">Why this report matters</h6>
        <p className="small text-muted mb-2">
          Owners Fund is where you spot irregularities first: if daily profit does not flow into the balance-sheet owner fund,
          something is wrong in the books. Use this with P&amp;L and balance sheet to find loss-making days, heavy expenses, and ledger gaps.
        </p>
        <p className="small mb-0">
          Same source as{' '}
          <a href="/other-reports/ownerfunds_statement" target="_blank" rel="noopener noreferrer">Owners Fund Statement</a>
          {' '}— column 1 should equal column 3 + column 2 each day.
        </p>
      </div>

      <div className="d-flex flex-wrap gap-2 align-items-end mb-3 owner-fund-analytics__toolbar">
        <div>
          <label className="form-label small mb-1">From</label>
          <input type="date" className="form-control form-control-sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <label className="form-label small mb-1">To</label>
          <input type="date" className="form-control form-control-sm" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={load} disabled={loading || !fromDate || !toDate}>
          {loading ? 'Analysing…' : (data ? 'Refresh analysis' : 'Load analysis')}
        </button>
        <div className="owner-fund-analytics__toolbar-actions ms-auto d-flex flex-wrap gap-2">
          <AdminPumpReportExportButton
            targetId={ADMIN_PUMP_EXPORT_IDS.ownerFundAnalytics}
            filename={`pump-${pumpId}-owners-fund-analytics.pdf`}
            label="Download PDF report"
            disabled={!data || loading}
            metadata={{
              title: `${pumpName || 'Pump'} — Owner's Fund Analytics`,
              subtitle: fromDate && toDate ? `${fromDate} to ${toDate}` : '',
            }}
            message="Capturing charts, KPIs, and insights. Please wait."
          />
          {onOpenAccounting && (summary.mismatch_day_count || 0) > 0 && (
            <button type="button" className="btn btn-outline-warning btn-sm" onClick={onOpenAccounting}>
              Fix in Accounting Integrity →
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {!data && !loading && !error && (
        <div className="admin-panel text-center py-5">
          <p className="text-muted mb-3">
            Choose a date range and click <strong>Load analysis</strong> to run owner fund analytics for {pumpName || 'this pump'}.
          </p>
        </div>
      )}

      {loading && !data && (
        <p className="text-muted text-center py-5">Loading owner fund analytics for {pumpName || 'pump'}…</p>
      )}

      {data && (
        <>
          <div className="owner-fund-kpi-grid mb-3">
            <div className={`owner-fund-kpi owner-fund-kpi--health owner-fund-kpi--${healthTone}`}>
              <span className="owner-fund-kpi__value">{healthScore}</span>
              <span className="owner-fund-kpi__label">Fund health</span>
              <span className="owner-fund-kpi__sub">{summary.health_label}</span>
            </div>
            <div className="owner-fund-kpi">
              <span className="owner-fund-kpi__value">{formatCompact(summary.period_pl_net)}</span>
              <span className="owner-fund-kpi__label">Period P&amp;L net</span>
              <span className="owner-fund-kpi__sub">{pl.margin_pct != null ? `${pl.margin_pct}% margin` : '—'}</span>
            </div>
            <div className="owner-fund-kpi">
              <span className="owner-fund-kpi__value">{formatCompact(summary.closing_system_owner_fund)}</span>
              <span className="owner-fund-kpi__label">Closing owner fund (BS)</span>
              <span className="owner-fund-kpi__sub">Trend: {summary.owner_fund_trend || '—'}</span>
            </div>
            <div className={`owner-fund-kpi ${summary.mismatch_day_count ? 'owner-fund-kpi--warn' : 'owner-fund-kpi--good'}`}>
              {(summary.mismatch_day_count || 0) > 0 ? (
                <button
                  type="button"
                  className="owner-fund-kpi__value owner-fund-kpi__value--clickable"
                  onClick={() => openMismatchDetail(topMismatchDate)}
                  title="Click to inspect largest mismatch"
                >
                  {summary.mismatch_day_count}
                </button>
              ) : (
                <span className="owner-fund-kpi__value">{summary.mismatch_day_count || 0}</span>
              )}
              <span className="owner-fund-kpi__label">Mismatch days</span>
              {(summary.total_mismatch_amount || 0) >= 0.01 ? (
                <button
                  type="button"
                  className="owner-fund-kpi__sub owner-fund-mismatch-link owner-fund-mismatch-link--inline"
                  onClick={() => openMismatchDetail(topMismatchDate)}
                >
                  {formatCompact(summary.total_mismatch_amount)} gap — view detail
                </button>
              ) : (
                <span className="owner-fund-kpi__sub">No gap</span>
              )}
            </div>
            <div className="owner-fund-kpi">
              <span className="owner-fund-kpi__value">{summary.profit_days || 0} / {summary.loss_days || 0}</span>
              <span className="owner-fund-kpi__label">Profit vs loss days</span>
              <span className="owner-fund-kpi__sub">{summary.flat_days || 0} flat days</span>
            </div>
          </div>

          {insights.length > 0 && (
            <div className="owner-fund-insights mb-3">
              <h6 className="owner-fund-section-title">Actionable insights</h6>
              <div className="owner-fund-insights__list">
                {insights.map((item, idx) => (
                  <div key={`${item.title}-${idx}`} className={`owner-fund-insight owner-fund-insight--${severityClass(item.severity)}`}>
                    <div className="owner-fund-insight__head">
                      <span className="owner-fund-insight__badge">{item.severity}</span>
                      <strong>{item.title}</strong>
                    </div>
                    <p className="owner-fund-insight__msg">{item.message}</p>
                    {item.action && <p className="owner-fund-insight__action small mb-0"><strong>Next:</strong> {item.action}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="row g-3 mb-3">
            <div className="col-lg-8">
              <div className="admin-panel h-100">
                <h6 className="owner-fund-section-title">Daily owner fund vs expected (Owners Fund Statement)</h6>
                <p className="small text-muted">
                  Green line = expected (prior fund + P&amp;L). Blue = balance-sheet system fund.
                  {' '}<strong>Click orange mismatch bars</strong> to see what went wrong.
                </p>
                {dailySeries.length > 0 ? (
                  <ChartWrap height={300}>
                    <ComposedChart data={dailySeries} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date_label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tickFormatter={formatCompact} tick={{ fontSize: 11 }} width={56} />
                      <Tooltip
                        formatter={(val, name) => [formatCurrency(val), name]}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ''}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="expected_owner_fund" name="Expected fund" stroke="#16a34a" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="system_owner_fund" name="System fund (BS)" stroke="#2563eb" strokeWidth={2} dot={false} />
                      <Bar
                        dataKey="difference"
                        name="Mismatch ₹"
                        fill="#f97316"
                        opacity={0.85}
                        cursor="pointer"
                        onClick={handleChartMismatchClick}
                      />
                    </ComposedChart>
                  </ChartWrap>
                ) : (
                  <p className="text-muted small">No daily movement in this period.</p>
                )}
              </div>
            </div>
            <div className="col-lg-4">
              <div className="admin-panel h-100">
                <h6 className="owner-fund-section-title">Daily P&amp;L</h6>
                <p className="small text-muted">Loss days (red) erode owner fund — investigate credit, shrinkage, and unposted sales.</p>
                {dailySeries.length > 0 ? (
                  <ChartWrap height={300}>
                    <BarChart data={dailySeries} margin={{ top: 8, right: 8, left: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date_label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                      <YAxis tickFormatter={formatCompact} tick={{ fontSize: 10 }} width={52} />
                      <Tooltip formatter={(val) => [formatCurrency(val), 'Daily P&L']} />
                      <Bar dataKey="daily_pl" name="Daily P&L" fill="#6366f1" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ChartWrap>
                ) : (
                  <p className="text-muted small">No P&amp;L days in range.</p>
                )}
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-6">
              <div className="admin-panel h-100">
                <h6 className="owner-fund-section-title">P&amp;L by category</h6>
                <p className="small text-muted">Income (green) vs expense drag (red) — focus on largest red bars to improve profit.</p>
                {plChartData.length > 0 ? (
                  <ChartWrap height={280}>
                    <BarChart data={plChartData} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tickFormatter={formatCompact} tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(val, _, props) => [formatCurrency(val), props.payload.fullName]} />
                      <Bar dataKey="net" radius={[0, 3, 3, 0]}>
                        {plChartData.map((entry, index) => (
                          <Cell key={`pl-cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ChartWrap>
                ) : (
                  <p className="text-muted small">No P&amp;L categories in period.</p>
                )}
                <div className="owner-fund-pl-totals mt-2">
                  <span>Income {formatCurrency(pl.total_income)}</span>
                  <span>Expenses {formatCurrency(pl.total_expenses)}</span>
                  <span className={pl.net_profit >= 0 ? 'text-success' : 'text-danger'}>
                    Net {formatCurrency(pl.net_profit)}
                  </span>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="admin-panel h-100">
                <h6 className="owner-fund-section-title">Balance sheet snapshot</h6>
                <p className="small text-muted">As of {bs.as_of_date || toDate} — owner fund plug from approved ledger.</p>
                <div className="owner-fund-bs-summary mb-2">
                  <div>
                    <span className="text-muted small">Owner fund</span>
                    <div className="fw-bold">{formatCurrency(bs.owner_fund_signed)}</div>
                    <span className={`badge bg-${bs.side === 'profit' ? 'success' : bs.side === 'loss' ? 'danger' : 'secondary'}`}>
                      {bs.side || 'balanced'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted small">Dr total</span>
                    <div>{formatCurrency(bs.dr_total)}</div>
                  </div>
                  <div>
                    <span className="text-muted small">Cr total</span>
                    <div>{formatCurrency(bs.cr_total)}</div>
                  </div>
                </div>
                <p className="small">{bs.interpretation}</p>
                {expensePieData.length > 0 && (
                  <>
                    <h6 className="small fw-bold mt-3">Top expenses (P&amp;L)</h6>
                    <ChartWrap height={180}>
                      <BarChart data={expensePieData} margin={{ top: 4, right: 8, left: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={56} />
                        <YAxis tickFormatter={formatCompact} tick={{ fontSize: 10 }} width={48} />
                        <Tooltip formatter={(val) => [formatCurrency(val), 'Expense']} />
                        <Bar dataKey="amount" fill="#ea580c" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ChartWrap>
                  </>
                )}
                {(bs.asset_heads?.length > 0 || bs.liability_heads?.length > 0) && (
                  <div className="row g-2 mt-2">
                    <div className="col-6">
                      <h6 className="small fw-bold">Key assets</h6>
                      <ul className="owner-fund-head-list">
                        {(bs.asset_heads || []).slice(0, 5).map((h) => (
                          <li key={h.slug + h.name}><span>{h.name}</span><span>{formatCurrency(h.net)}</span></li>
                        ))}
                      </ul>
                    </div>
                    <div className="col-6">
                      <h6 className="small fw-bold">Key liabilities</h6>
                      <ul className="owner-fund-head-list">
                        {(bs.liability_heads || []).slice(0, 5).map((h) => (
                          <li key={h.slug + h.name}><span>{h.name}</span><span>{formatCurrency(h.net)}</span></li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {mismatchDays.length > 0 && (
            <div className="admin-panel mb-3">
              <h6 className="owner-fund-section-title">Mismatch days — click gap to investigate</h6>
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Gap ₹</th>
                      <th>Daily P&amp;L</th>
                      <th>Expected fund</th>
                      <th>System fund</th>
                      <th>BS movement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mismatchDays.map((row) => (
                      <tr key={row.date} className="owner-fund-mismatch-row">
                        <td>
                          <button type="button" className="owner-fund-mismatch-link owner-fund-mismatch-link--date" onClick={() => openMismatchDetail(row.date)}>
                            {formatReportDate(row.date)}
                          </button>
                        </td>
                        <td>
                          <MismatchLink amount={row.difference} date={row.date} onOpen={openMismatchDetail} className="fw-bold" />
                        </td>
                        <td>{formatCurrency(row.daily_pl)}</td>
                        <td>{formatCurrency(row.expected_owner_fund)}</td>
                        <td>{formatCurrency(row.system_owner_fund)}</td>
                        <td>{formatCurrency(row.daily_bs_movement)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(data.statement_rows || []).length > 0 && (
            <details className="admin-panel owner-fund-statement-details">
              <summary className="owner-fund-section-title mb-0" style={{ cursor: 'pointer' }}>
                Full Owners Fund Statement rows ({data.statement_rows.length})
              </summary>
              <div className="table-responsive mt-2">
                <table className="table table-sm table-striped mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Prior system fund</th>
                      <th>Daily P&amp;L</th>
                      <th>Expected closing</th>
                      <th>System fund (BS)</th>
                      <th>Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.statement_rows.map((row) => (
                      <tr key={row.ac_date} className={row.difference >= 0.01 ? 'table-warning' : ''}>
                        <td>{formatReportDate(row.ac_date)}</td>
                        <td>{formatCurrency(row.profit_loss_owners_fund)}</td>
                        <td>{formatCurrency(row.profit_loss)}</td>
                        <td>{formatCurrency(row.owners_fund)}</td>
                        <td>{formatCurrency(row.system_owners_fund)}</td>
                        <td>
                          <MismatchLink amount={row.difference} date={row.ac_date} onOpen={openMismatchDetail} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </>
      )}

      {mismatchModalDate && (
        <OwnerFundMismatchDetailModal
          pumpId={pumpId}
          acDate={mismatchModalDate}
          fromDate={fromDate}
          toDate={toDate}
          onClose={() => setMismatchModalDate('')}
        />
      )}
    </div>
  );
}
